import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { History, Download, RotateCcw, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { isOfficeFile } from './OfficeEditButton';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const VersionRow = ({ v, onDownload, onRestore, busy }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-3" data-testid={`office-version-${v.version}`}>
    <div className="min-w-0">
      <p className="text-sm font-medium flex items-center gap-2">
        Version {v.version}
        {v.is_current && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">aktuell</Badge>}
      </p>
      <p className="text-xs text-muted-foreground truncate">{fmt(v.uploaded_at)} · {v.uploaded_by || '—'}{v.note ? ` · ${v.note}` : ''}</p>
    </div>
    <div className="flex gap-1 shrink-0">
      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => onDownload(v.version)} title="Diese Version herunterladen" data-testid={`office-version-download-${v.version}`}>
        <Download className="h-4 w-4" />
      </Button>
      {!v.is_current && (
        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" disabled={busy} onClick={() => onRestore(v.version)} title="Wiederherstellen" data-testid={`office-version-restore-${v.version}`}>
          <RotateCcw className="h-4 w-4" />
        </Button>
      )}
    </div>
  </div>
);

export const OfficeVersionsDialog = ({ open, onOpenChange, source, id, fileType, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const base = `/office-edit/${source}/${id}/versions`;

  const load = useCallback(async () => {
    try { setData((await api.get(base)).data); } catch { toast.error('Versionen konnten nicht geladen werden'); }
  }, [base]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const download = async (version) => {
    try {
      const res = await api.get(`${base}/${version}/download`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = (data?.filename || 'Dokument').replace(/\.(\w+)$/, `_v${version}.$1`);
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error((typeof e.response?.data?.detail === 'string' && e.response.data.detail) || 'Aktion fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const restore = (version) => {
    if (!window.confirm(`Version ${version} wiederherstellen? Sie wird als neue Version gespeichert – nichts geht verloren.`)) return;
    run(() => api.post(`${base}/${version}/restore`), `Version ${version} wiederhergestellt`);
  };

  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    run(() => api.post(base, fd, { headers: { 'Content-Type': 'multipart/form-data' } }), 'Neue Version gespeichert');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="office-versions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Versionen</DialogTitle>
          <DialogDescription className="truncate">{data?.filename || '…'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {!data ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : data.versions.map((v) => (
            <VersionRow key={v.version} v={v} busy={busy} onDownload={download} onRestore={restore} />
          ))}
        </div>
        {isOfficeFile(fileType) && (
          <div className="pt-2 border-t">
            <input ref={fileRef} type="file" accept={`.${fileType}`} className="hidden" onChange={upload} data-testid="office-version-file-input" />
            <Button variant="outline" size="sm" className="rounded-full" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="office-version-upload-btn">
              <Upload className="h-4 w-4 mr-1" /> Neue Version hochladen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const OfficeVersionsButton = ({ source, id, fileType, onChanged }) => {
  const [open, setOpen] = useState(false);
  if (!isOfficeFile(fileType)) return null;
  return (
    <>
      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => setOpen(true)} title="Versionen" data-testid={`office-versions-${source}-${id}`}>
        <History className="h-4 w-4" />
      </Button>
      {open && <OfficeVersionsDialog open={open} onOpenChange={setOpen} source={source} id={id} fileType={fileType} onChanged={onChanged} />}
    </>
  );
};
